import React from "react";
import { useTranslation } from "react-i18next";
import Select, { components, MultiValueRemoveProps } from "react-select";
import { MultiSelectDropdownProps } from "../../types/screensTypes";
import { getCustomStyles } from "./Dropdown";
import dropdownStyles from "./Dropdown.module.css";
import styles from "./MultiSelectDropdown.module.css";
import Icon from "./Icon";

const MultiValueRemove = (props: MultiValueRemoveProps) => (
    <components.MultiValueRemove {...props}>
        <span className={styles.chipRemoveInner} aria-hidden>
            <Icon name="ic-x" className="ic" />
        </span>
    </components.MultiValueRemove>
);

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
    label,
    options,
    onChange,
    loading,
    error,
    disabled,
    value = [],
    placeholder,
    isMissing = false,
    isWithoutMargin = false,
    onBlur,
    menuPlacement,
    maxHeight,
    className = "",
    fullWidth = false,
    maxWidth = "none",
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const placeholderText = placeholder || t("selectOptions");

    const widthClass = fullWidth ? (maxWidth === "form" ? dropdownStyles.fullWidthForm : dropdownStyles.fullWidth) : "";

    const rootClass = [
        isWithoutMargin ? `${dropdownStyles.withoutMargin} dropdown-without-margin` : `${dropdownStyles.root} dropdown`,
        styles.root,
        widthClass,
        className,
    ]
        .filter(Boolean)
        .join(" ");

    const selectedOptions = options.filter((option) => value.includes(option.value));

    const handleChange = (selected: any) => {
        if (selected) {
            onChange(selected.map((option: { value: string; label: string }) => option.value));
        } else {
            onChange([]);
        }
    };

    const selectStyles = {
        ...getCustomStyles(isMissing, { isMulti: true }),
        menu: (provided: any) => ({
            ...provided,
            zIndex: 11000,
            borderRadius: "var(--radius-sm, 7px)",
            border: "1px solid var(--line)",
            boxShadow: "var(--shadow-md, 0 10px 30px rgba(0,0,0,.12))",
            overflow: "hidden",
            ...(maxHeight && { maxHeight, overflow: "auto" }),
        }),
    };

    return (
        <div className={rootClass} data-rw-control="dropdown">
            {label && <label>{label}</label>}
            {loading ? (
                <p className="error-message" style={{ color: "var(--ink-3)" }}>
                    {t("loading")}
                </p>
            ) : error ? (
                <p className="error-message">{t("errorLoadingOptions")}</p>
            ) : (
                <Select
                    isMulti
                    options={options}
                    value={selectedOptions}
                    onChange={handleChange}
                    isDisabled={disabled}
                    placeholder={placeholderText}
                    classNamePrefix="rw-select"
                    onBlur={onBlur}
                    hideSelectedOptions={false}
                    closeMenuOnSelect={false}
                    styles={selectStyles}
                    menuPosition="fixed"
                    menuPortalTarget={document.body}
                    components={{ MultiValueRemove }}
                    {...(menuPlacement && { menuPlacement })}
                />
            )}
        </div>
    );
};

export default MultiSelectDropdown;
